/* =========================================
   START OF FILE App.js
   ========================================= */

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import {
  Plus,
  Edit2,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Grid,
  DollarSign,
  TriangleAlert,
  Clock,
  Calendar,
  CheckCircle,
  AlertCircle,
  Check,
  Search,
} from "lucide-react";
import "./App.css";

// --- SUPABASE CONFIG ---
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;

const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

/* =========================================
              2. CONSTANTS & CONFIGURATION
              ========================================= */

// Comment 7: Updated Capacities for C61 (16) and V308 (20)
const ROOM_TIERS = {
  small: { id: "small", name: "細房", desc: "4人", capacity: 4 },
  medium: { id: "medium", name: "中房", desc: "6人", capacity: 6 },
  large: { id: "large", name: "大房", desc: "12人", capacity: 12 },
  vip: { id: "vip", name: "VIP房", desc: "派對房", capacity: 20 },
};

// Comment 2: Rooms reserved for Walk-in only (Hidden from Online Booking)
const WALK_IN_ONLY_ROOMS = ["C66", "B52"];

const ALL_ROOMS = [
  { id: "B11", type: "small" },
  { id: "B12", type: "small" },
  { id: "B13", type: "medium" },
  { id: "B15", type: "small" },
  { id: "B16", type: "medium" },
  { id: "B17", type: "large" },
  { id: "B18", type: "small" },
  { id: "B19", type: "large" },
  { id: "B20", type: "medium" },
  { id: "B21", type: "small" },
  { id: "B22", type: "medium" },
  { id: "B23", type: "small" },
  { id: "B25", type: "medium" },
  { id: "B26", type: "medium" },
  { id: "B27", type: "medium" },
  { id: "B28", type: "large" },
  { id: "B29", type: "medium" },
  { id: "B30", type: "medium" },
  { id: "B31", type: "large" },
  { id: "B32", type: "small" },
  { id: "B33", type: "small" },
  { id: "B35", type: "small" },
  { id: "B36", type: "large" },
  { id: "B37", type: "large" },
  { id: "B38", type: "large" },
  { id: "B39", type: "large" },
  { id: "B50", type: "small" },
  { id: "B51", type: "small" },
  { id: "B52", type: "small" }, // Walk-in
  { id: "C60", type: "vip" },
  { id: "C61", type: "vip" },
  { id: "C62", type: "large" },
  { id: "C63", type: "medium" },
  { id: "C66", type: "small" }, // Walk-in
  { id: "C67", type: "small" },
  { id: "C68", type: "small" },
  { id: "V308", type: "vip" },
  { id: "V328", type: "vip" },
  { id: "V338", type: "vip" },
];

const SESSIONS = [
  { id: "lunch", label: "11:00 - 15:00", type: "lunch" },
  { id: "afternoon", label: "15:00 - 19:00", type: "lunch" },
  { id: "night", label: "19:00 - 04:00", type: "night" },
];

const HK_HOLIDAYS = [
  // --- 2025 ---
  "2025-01-01", // New Year
  "2025-01-29",
  "2025-01-30",
  "2025-01-31", // Lunar New Year
  "2025-04-04", // Ching Ming
  "2025-04-18",
  "2025-04-19",
  "2025-04-21", // Easter
  "2025-05-01", // Labour Day
  "2025-05-05", // Buddha's Birthday
  "2025-05-31", // Tuen Ng
  "2025-07-01", // SAR Day
  "2025-10-01", // National Day
  "2025-10-07", // Day after Mid-Autumn
  "2025-10-29", // Chung Yeung
  "2025-12-25",
  "2025-12-26", // Christmas

  // --- 2026 ---
  "2026-01-01",
  "2026-02-17",
  "2026-02-18",
  "2026-02-19", // LNY
  "2026-04-03",
  "2026-04-04",
  "2026-04-06", // Easter
  "2026-04-07", // Day following Ching Ming (Substituted due to Easter Clash)
  "2026-05-01",
  "2026-05-25", // Buddha
  "2026-06-19", // Tuen Ng
  "2026-07-01",
  "2026-09-26", // Day after Mid-Autumn
  "2026-10-01",
  "2026-10-19", // Chung Yeung
  "2026-12-25",
  "2026-12-26",

  // --- 2027 ---
  "2027-01-01",
  "2027-02-06",
  "2027-02-08",
  "2027-02-09", // LNY (Day 2 is Sun, so Day 4 is holiday)
  "2027-03-26",
  "2027-03-27",
  "2027-03-29", // Easter
  "2027-04-05", // Ching Ming
  "2027-05-01",
  "2027-05-14", // Buddha
  "2027-06-09", // Tuen Ng
  "2027-07-01",
  "2027-09-16", // Day after Mid-Autumn
  "2027-10-01",
  "2027-10-09", // Chung Yeung
  "2027-12-25",
  "2027-12-27", // Christmas (26 is Sun, so 27 is holiday)

  // --- 2028 ---
  "2028-01-01",
  "2028-01-26",
  "2028-01-27",
  "2028-01-28", // LNY
  "2028-04-04", // Ching Ming
  "2028-04-14",
  "2028-04-15",
  "2028-04-17", // Easter
  "2028-05-01",
  "2028-05-02", // Buddha
  "2028-05-29", // Tuen Ng
  "2028-07-01",
  "2028-10-02", // Day after National Day (Oct 1 is Sun)
  "2028-10-04", // Day after Mid-Autumn
  "2028-10-26", // Chung Yeung
  "2028-12-25",
  "2028-12-26",
];

const TEXT = {
  tc: {
    title: "SUGAR CEO",
    sub: "糖果總裁 KTV",
    date: "日期",
    arrTime: "到達時間",
    session: "時段",
    roomHeader: "選擇房間",
    name: "稱呼",
    phone: "電話",
    pax: "人數",
    remark: "備註",
    book: "確認預約",
    staffTitle: "管理控制台",
    walkIn: "新增預約 / Walk-In",
    days: ["日", "一", "二", "三", "四", "五", "六"],
    priceTable: "價目表",
    status: "房間狀況",
    save: "確認更改",
    edit: "更改預約",
    unavailable: "Unavailable",
    assignRoom: "分配房間",
  },
};

/* =========================================
              3. LOGIC & HELPERS
              ========================================= */

const determineSession = (timeStr) => {
  if (!timeStr) return SESSIONS[0];
  const [h] = timeStr.split(":").map(Number);
  const adjH = h < 10 ? h + 24 : h;
  if (adjH >= 11 && adjH < 15) return SESSIONS[0]; // Lunch
  if (adjH >= 15 && adjH < 19) return SESSIONS[1]; // Afternoon
  return SESSIONS[2]; // Night
};

// Helper to shift date back by 1 day if time is 00:00 - 04:00
// Used ONLY for Customer Page inputs
const adjustForLateNight = (dateStr, timeStr) => {
  const [h] = timeStr.split(":").map(Number);
  // If hour is 0, 1, 2, or 3 (up to 03:59)
  if (h < 4) {
    const d = new Date(dateStr);
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  }
  return dateStr;
};

const getPriceForRoom = (dateStr, sessionLabel, roomID, priceData) => {
  if (!priceData || priceData.length === 0) return "---";

  const date = new Date(dateStr);
  const day = date.getDay(); // 0 = Sunday
  const isHoliday = HK_HOLIDAYS.includes(dateStr);

  const tomorrow = new Date(date);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0];
  const isHolidayEve = HK_HOLIDAYS.includes(tomorrowStr);

  const sessionObj = SESSIONS.find((s) => s.label === sessionLabel);
  const sessionType = sessionObj ? sessionObj.type : "lunch";
  const dbSession = sessionType === "night" ? "night" : "day";

  let category = "weekday";

  if (sessionType === "night") {
    if (isHolidayEve) category = "holidayEve";
    else if (day === 5) category = "friday";
    else if (day === 6) category = "saturday";
    else if (day === 0) category = "sunday";
    else category = "weekday";
  } else {
    if (isHoliday) category = "holiday";
    else if (day === 6) category = "saturday";
    else if (day === 0) category = "sunday";
    else if (day === 5) category = "friday";
    else category = "weekday";
  }

  let row = priceData.find(
    (p) => p.category === category && p.session === dbSession
  );

  if (!row) {
    if (category === "saturday" || category === "sunday") {
      row = priceData.find(
        (p) => p.category === "weekday" && p.session === dbSession
      );
    }
  }

  return row && row[roomID] !== null ? row[roomID] : "---";
};

/* =========================================
              4. MAIN CONTAINER
              ========================================= */

function AppContainer() {
  const [bookings, setBookings] = useState([]);
  const [prices, setPrices] = useState([]);
  const [session, setSession] = useState(null);

  const fetchPrices = async () => {
    const { data } = await supabase
      .from("pricing")
      .select("*")
      .order("id", { ascending: true });
    if (data) setPrices(data);
  };

  const fetchBookings = async () => {
    // 1. Calculate a cutoff date (e.g., 14 days ago)
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 5);
    const cutoffString = cutoffDate.toISOString().split("T")[0];

    // 2. Fetch only recent and future bookings
    const { data, error } = await supabase
      .from("bookings")
      .select("*")
      .gte("booking_date", cutoffString) // Only fetch if date >= 14 days ago
      .limit(3000) // Explicitly set a higher limit just in case
      .order("booking_date", { ascending: true });

    if (error) console.error("Error fetching bookings:", error.message);
    setBookings(data || []);
  };

  useEffect(() => {
    fetchBookings();
    fetchPrices();

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => setSession(session));
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));

    const bookingChannel = supabase
      .channel("realtime-bookings")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "bookings" },
        fetchBookings
      )
      .subscribe();

    const priceChannel = supabase
      .channel("realtime-prices")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "pricing" },
        fetchPrices
      )
      .subscribe();

    return () => {
      supabase.removeChannel(bookingChannel);
      supabase.removeChannel(priceChannel);
      subscription.unsubscribe();
    };
  }, []);

  // Updated addBooking to handle Admin instant confirmation
  const addBooking = async (form, isAdmin = false) => {
    if (!form.name || !form.phone) {
      alert("名字和電話是必填的。");
      return null;
    }

    const newBooking = {
      booking_date: form.date,
      start_time: form.session,
      arrival_time: form.arrTime,
      room_type: form.roomType,
      client_name: form.name,
      client_phone: form.phone,
      pax: form.pax,
      remarks: form.remarks,
      // If Admin: Assign room immediately, otherwise empty
      assigned_room: isAdmin ? form.assigned_room : "",
      duration: 0,
      // If Admin: Confirm immediately, otherwise Pending
      status: isAdmin ? "confirmed" : "pending",
    };

    const { data, error } = await supabase
      .from("bookings")
      .insert([newBooking])
      .select();

    if (error) {
      alert("Error: " + error.message);
      return null;
    }

    if (data && data[0]) {
      setBookings((prev) => [...prev, data[0]]);
      return data[0].id;
    }
    return null;
  };

  const updateBooking = async (id, form) => {
    const updates = {
      booking_date: form.date,
      start_time: form.session,
      arrival_time: form.arrTime,
      room_type: form.roomType,
      client_name: form.name,
      client_phone: form.phone,
      pax: form.pax,
      remarks: form.remarks,
      assigned_room: form.assigned_room,
    };
    setBookings((prev) =>
      prev.map((b) => (b.id === id ? { ...b, ...updates } : b))
    );
    await supabase.from("bookings").update(updates).eq("id", id);
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Confirm delete?")) return;
    setBookings((prev) => prev.filter((b) => b.id !== id));
    await supabase.from("bookings").delete().eq("id", id);
  };

  const approveBooking = async (id, assignedRoom = null) => {
    setBookings((prev) =>
      prev.map((b) =>
        b.id === id
          ? { ...b, status: "confirmed", assigned_room: assignedRoom }
          : b
      )
    );
    const { error } = await supabase
      .from("bookings")
      .update({
        status: "confirmed",
        assigned_room: assignedRoom,
      })
      .eq("id", id);

    if (error) {
      alert("Error approving: " + error.message);
    }
  };

  return (
    <BrowserRouter>
      <div className="cyber-bg"></div>
      <Routes>
        <Route
          path="/"
          element={
            <CustomerSinglePage
              lang="tc"
              t={TEXT["tc"]}
              bookings={bookings}
              onBook={addBooking}
              prices={prices}
            />
          }
        />
        <Route
          path="/staff"
          element={session ? <Navigate to="/admin" /> : <StaffLogin />}
        />
        <Route
          path="/admin"
          element={
            session ? (
              <StaffDashboard
                lang="tc"
                t={TEXT["tc"]}
                bookings={bookings}
                onDelete={deleteBooking}
                onAdd={addBooking}
                onEdit={updateBooking}
                prices={prices}
                onUpdatePrices={fetchPrices}
                onApprove={approveBooking}
              />
            ) : (
              <Navigate to="/staff" />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

/* =========================================
              5. PAGES & DASHBOARDS
              ========================================= */

function StaffLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setLoading(false);
    if (error) alert("Login Failed: " + error.message);
  };

  return (
    <div className="panel login-box">
      <h2 className="glow-text" style={{ marginBottom: "30px" }}>
        SYSTEM ACCESS
      </h2>
      <form onSubmit={handleLogin}>
        <input
          type="email"
          placeholder="Admin Email"
          className="login-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          type="password"
          placeholder="Password"
          className="login-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button type="submit" className="mega-btn" disabled={loading}>
          {loading ? "VERIFYING..." : "LOGIN"}
        </button>
      </form>
    </div>
  );
}

function StaffDashboard({
  t,
  bookings,
  onDelete,
  onAdd,
  onEdit,
  prices,
  onUpdatePrices,
  onApprove,
}) {
  const [filterDate, setFilterDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  // Comment 3: Lifted Filter State
  const [roomFilter, setRoomFilter] = useState("ALL");
  // Comment 4: Search State
  const [searchTerm, setSearchTerm] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [pendingModalOpen, setPendingModalOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [tooltipData, setTooltipData] = useState(null);

  const pendingBookings = bookings.filter((b) => b.status === "pending");
  const confirmedBookings = bookings.filter((b) => b.status !== "pending");

  // Comment 4: Filter by Search Term (Ref, Name, Phone)
  const filteredBySearch = confirmedBookings.filter((b) => {
    if (!searchTerm) return true;
    const lowerTerm = searchTerm.toLowerCase();
    const idMatch = String(b.id).includes(lowerTerm);
    const nameMatch = (b.client_name || "").toLowerCase().includes(lowerTerm);
    const phoneMatch = (b.client_phone || "").includes(lowerTerm);
    return idMatch || nameMatch || phoneMatch;
  });

  // Filter by Date
  let todaysBookings = filteredBySearch.filter(
    (b) => b.booking_date === filterDate
  );

  todaysBookings.sort((a, b) =>
    (a.booking_date + a.start_time).localeCompare(b.booking_date + b.start_time)
  );

  // Comment 3: Apply Room Filter to the list
  const applyRoomFilter = (list) => {
    if (roomFilter === "ALL") return list;
    if (roomFilter === "vip") {
      return list.filter((b) => b.room_type === "vip"); // Simplified
    }
    return list.filter((b) => b.room_type === roomFilter);
  };

  const filteredTodaysBookings = applyRoomFilter(todaysBookings);

  const bookingsS1 = filteredTodaysBookings.filter((b) =>
    b.start_time.startsWith("11:00")
  );
  const bookingsS2 = filteredTodaysBookings.filter((b) =>
    b.start_time.startsWith("15:00")
  );
  const bookingsS3 = filteredTodaysBookings.filter((b) =>
    b.start_time.startsWith("19:00")
  );

  const clearTooltip = () => setTooltipData(null);

  const handleCloseModal = () => {
    setModalOpen(false);
    setPriceModalOpen(false);
    setPendingModalOpen(false);
    clearTooltip();
  };

  const renderCard = (b, sessionColor) => {
    const roomTypeName = ROOM_TIERS[b.room_type]?.name || b.room_type;
    const roomName = b.assigned_room || "如未選擇，則自動分配";
    const isAssigned = !!b.assigned_room;
    const price = getPriceForRoom(
      b.booking_date,
      b.start_time,
      b.room_type,
      prices
    );

    return (
      <div
        key={b.id}
        className="hud-card"
        style={{ "--card-theme": sessionColor }}
        onMouseEnter={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setTooltipData({
            booking: b,
            roomTypeName,
            rect: rect,
            color: sessionColor,
            price: price,
          });
        }}
        onMouseLeave={() => setTooltipData(null)}
      >
        <div className="hud-time">
          <div className="hud-time-text">{b.arrival_time}</div>
          <div
            className="hud-sub-text"
            style={{ color: sessionColor, fontWeight: "bold", marginTop: 5 }}
          >
            {roomTypeName}
          </div>
        </div>

        <div className="hud-info">
          <div className="info-header">
            <span className="client-name">{b.client_name}</span>
            <span
              className={`room-number-badge ${isAssigned ? "assigned" : ""}`}
            >
              {roomName}
            </span>
          </div>

          <div className="info-details">
            {/* Comment 4: Show Reference ID */}
            <span style={{ color: "var(--neon-cyan)", fontWeight: "bold" }}>
              #{b.id}
            </span>
            <span style={{ opacity: 0.3 }}>|</span>
            <span>{b.pax} 人</span>
            <span style={{ opacity: 0.3 }}>|</span>
            <span style={{ fontFamily: "monospace" }}>{b.client_phone}</span>

            {b.remarks && (
              <>
                <span style={{ opacity: 0.3 }}>|</span>
                <div className="icon-alert-only">
                  <TriangleAlert size={14} />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="hud-actions">
          <button
            className="hud-btn"
            onClick={() => {
              setEditingBooking(b);
              setModalOpen(true);
            }}
          >
            <Edit2 size={16} />
          </button>
          <button className="hud-btn btn-del" onClick={() => onDelete(b.id)}>
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="container">
      <div
        className="dash-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
          height: "40px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <h2 className="glow-text" style={{ margin: 0 }}>
            {t.staffTitle}
          </h2>
          <span
            style={{
              color: "#888",
              fontFamily: "Orbitron",
              fontSize: "1.2rem",
            }}
          >
            {filterDate}
          </span>
          {/* Comment 4: Search Input */}
          <div style={{ position: "relative", marginLeft: "20px" }}>
            <Search
              size={14}
              style={{ position: "absolute", left: 10, top: 8, color: "#666" }}
            />
            <input
              type="text"
              placeholder="Search (Ref/名字/電話)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: "#111",
                border: "1px solid #333",
                padding: "5px 10px 5px 30px",
                borderRadius: "15px",
                width: "250px",
                fontSize: "0.85rem",
                color: "white",
              }}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          {pendingBookings.length > 0 && (
            <button
              onClick={() => setPendingModalOpen(true)}
              className="btn-pending"
            >
              <TriangleAlert size={14} /> 新預約 ({pendingBookings.length})
            </button>
          )}

          <button
            onClick={() => setPriceModalOpen(true)}
            className="btn-compact"
          >
            <DollarSign size={14} /> {t.priceTable}
          </button>

          <button
            onClick={() => {
              setEditingBooking(null);
              setModalOpen(true);
            }}
            style={{
              background: "var(--neon-blue)",
              border: "none",
              padding: "5px 15px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              borderRadius: "4px",
              color: "black",
            }}
          >
            <Plus size={14} /> {t.walkIn}
          </button>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="grid-area-cal">
          <div className="panel">
            <NeonCalendar
              selectedDate={filterDate}
              onSelect={setFilterDate}
              t={t}
              bookings={confirmedBookings}
            />
          </div>
        </div>

        <div className="grid-area-status">
          <RoomStatusGrid
            date={filterDate}
            bookings={confirmedBookings}
            t={t}
            // Comment 3: Pass lifted state and setter
            filter={roomFilter}
            onFilterChange={setRoomFilter}
          />
        </div>

        <div className="grid-col-list">
          <div
            className="list-header"
            style={{ color: "#00f3ff", borderBottomColor: "#00f3ff33" }}
          >
            11:00 - 15:00 ({bookingsS1.length})
          </div>
          <div className="list-scroll-area">
            {bookingsS1.length === 0 && (
              <div
                style={{ textAlign: "center", color: "#444", padding: "20px" }}
              >
                Empty
              </div>
            )}
            {bookingsS1.map((b) => renderCard(b, "#00f3ff"))}
          </div>
        </div>

        <div className="grid-col-list">
          <div
            className="list-header"
            style={{ color: "#0066ff", borderBottomColor: "#0066ff33" }}
          >
            15:00 - 19:00 ({bookingsS2.length})
          </div>
          <div className="list-scroll-area">
            {bookingsS2.length === 0 && (
              <div
                style={{ textAlign: "center", color: "#444", padding: "20px" }}
              >
                Empty
              </div>
            )}
            {bookingsS2.map((b) => renderCard(b, "#0066ff"))}
          </div>
        </div>

        <div className="grid-col-list">
          <div
            className="list-header"
            style={{ color: "#bc13fe", borderBottomColor: "#bc13fe33" }}
          >
            19:00 - 04:00 ({bookingsS3.length})
          </div>
          <div className="list-scroll-area">
            {bookingsS3.length === 0 && (
              <div
                style={{ textAlign: "center", color: "#444", padding: "20px" }}
              >
                Empty
              </div>
            )}
            {bookingsS3.map((b) => renderCard(b, "#bc13fe"))}
          </div>
        </div>
      </div>

      {tooltipData && (
        <div
          className="floating-tooltip"
          style={{
            top: tooltipData.rect.top,
            left: tooltipData.rect.left,
            transform: "translateY(-110%)",
            borderColor: tooltipData.color,
          }}
        >
          <div className="tooltip-grid">
            <div className="tt-label">日期</div>
            <div className="tt-value">{tooltipData.booking.booking_date}</div>
            <div className="tt-label">時段</div>
            <div className="tt-value" style={{ color: tooltipData.color }}>
              {tooltipData.booking.start_time}
            </div>
            <div className="tt-label">Ref</div>
            <div className="tt-value">#{tooltipData.booking.id}</div>
            <div className="tt-label">類型</div>
            <div className="tt-value">{tooltipData.roomTypeName}</div>
            <div className="tt-label">價錢</div>
            <div
              className="tt-value"
              style={{ color: "#fff", fontWeight: "bold" }}
            >
              ${tooltipData.price}
            </div>
            <div className="tt-label">電話</div>
            <div className="tt-value" style={{ fontFamily: "monospace" }}>
              {tooltipData.booking.client_phone}
            </div>
            {tooltipData.booking.remarks && (
              <>
                <div
                  className="tt-label"
                  style={{ color: "var(--amber-alert)" }}
                >
                  備註
                </div>
                <div
                  className="tt-value"
                  style={{ color: "var(--amber-alert)" }}
                >
                  {tooltipData.booking.remarks}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {modalOpen && (
        <StaffModal
          t={t}
          booking={editingBooking}
          onClose={handleCloseModal}
          bookings={bookings}
          onSave={async (f) => {
            if (editingBooking) {
              await onEdit(editingBooking.id, f);
            } else {
              // Pass true for isAdmin to skip pending list
              await onAdd(f, true);
            }
            handleCloseModal();
          }}
        />
      )}

      {priceModalOpen && (
        <PricingModal
          onClose={handleCloseModal}
          prices={prices}
          onSaveSuccess={onUpdatePrices}
        />
      )}

      {pendingModalOpen && (
        <PendingModal
          onClose={handleCloseModal}
          bookings={pendingBookings}
          confirmedBookings={confirmedBookings}
          onApprove={(id, room) => {
            onApprove(id, room);
          }}
          onDelete={onDelete}
          prices={prices}
          setTooltipData={setTooltipData}
        />
      )}
    </div>
  );
}

function CustomerSinglePage({ lang, t, bookings, onBook, prices }) {
  const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes()
    ).padStart(2, "0")}`;
  };

  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    arrTime: getCurrentTime(),
    session: "",
    roomType: "",
    name: "",
    phone: "",
    pax: 2,
    remarks: "",
  });

  const [successId, setSuccessId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const detectedSession = determineSession(form.arrTime);
    setForm((f) => ({ ...f, session: detectedSession.label }));
  }, [form.arrTime]);

  const getValidationErrors = () => {
    const errors = {};
    const [h] = form.arrTime.split(":").map(Number);
    if (h >= 4 && h < 11) {
      errors.time = "營業時間為 11:00 AM - 04:00 AM";
    }
    if (form.roomType) {
      const roomCap = ROOM_TIERS[form.roomType]?.capacity || 99;
      if (parseInt(form.pax) > roomCap) {
        errors.pax = `房間最多可容納 ${roomCap} 人`;
      }
    }
    return errors;
  };

  const errors = getValidationErrors();
  const hasErrors = Object.keys(errors).length > 0;

  const submit = async () => {
    if (hasErrors) return;
    if (!form.roomType) return alert("請選擇房間類型。");
    if (!form.name || !form.phone) return alert("請輸入您的名字和電話。");

    setLoading(true);

    // LOGIC CHANGE:
    // If customer selects 01:00 on Jan 30, they mean Saturday Morning.
    // We shift it back to Jan 29 (Friday Night) for the system.
    const finalDate = adjustForLateNight(form.date, form.arrTime);
    const submissionForm = { ...form, date: finalDate };

    const id = await onBook(submissionForm);
    setLoading(false);
    if (id) setSuccessId(id);
  };

  // Updated Availability Logic
  const checkAvail = (roomTypeId) => {
    // 1. Calculate Maximum Online Capacity for this specific room type
    // Filter ALL_ROOMS to find rooms of this type that are NOT in the Walk-In list
    const onlineInventory = ALL_ROOMS.filter(
      (r) => r.type === roomTypeId && !WALK_IN_ONLY_ROOMS.includes(r.id)
    );
    const maxCapacity = onlineInventory.length;

    // 2. Calculate Current "Online" Consumption
    // We count bookings that are taking up an "Online" slot.
    // If a booking is explicitly assigned to a Walk-In room (by admin), it does NOT count against online capacity.
    const currentConsumption = bookings.filter((b) => {
      // Must match Date, Session, and Room Type
      if (b.booking_date !== form.date || b.start_time !== form.session)
        return false;
      if (b.room_type !== roomTypeId) return false;

      // If the booking is already assigned to a restricted Walk-In room (e.g., C66),
      // it is NOT consuming an online slot.
      if (b.assigned_room && WALK_IN_ONLY_ROOMS.includes(b.assigned_room)) {
        return false;
      }

      // Otherwise (Pending, Unassigned Confirmed, or Assigned to Regular Room), it counts.
      return true;
    }).length;

    return currentConsumption < maxCapacity;
  };

  if (successId) {
    return (
      <div
        className="customer-container"
        style={{ textAlign: "center", paddingTop: "15vh" }}
      >
        <div className="panel success-panel-wrapper">
          <CheckCircle
            size={64}
            color="#00f3ff"
            style={{ margin: "0 auto 20px auto" }}
          />
          <h1 className="glow-text" style={{ fontSize: "1.8rem" }}>
            BOOKING SUBMITTED
          </h1>
          <h3 style={{ color: "white" }}>Ref: #{successId}</h3>

          {/* Comment 1: Message about WhatsApp confirmation */}
          <div
            style={{
              color: "#aaa",
              fontSize: "0.9rem",
              margin: "20px 0",
              lineHeight: "1.5",
            }}
          >
            感謝您的預約。
            <br />
            我們收到您的申請後，會透過{" "}
            <span style={{ color: "#25d366", fontWeight: "bold" }}>
              WhatsApp
            </span>{" "}
            聯絡您確認預約。
            <br />
            請留意您的手機訊息，只有收到確認訊息後預約才算成功。
          </div>

          <a
            href={`https://wa.me/85298406406?text=Ref ${successId}`}
            className="mega-btn"
            target="_blank"
            rel="noreferrer"
            style={{
              textDecoration: "none",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
            }}
          >
            WhatsApp Us
          </a>

          <button
            onClick={() => {
              setSuccessId(null);
              setForm({ ...form, name: "", phone: "" });
            }}
            style={{
              background: "none",
              border: "none",
              color: "#666",
              cursor: "pointer",
              textDecoration: "underline",
              marginTop: 15,
            }}
          >
            再次預約
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-container">
      <div className="cust-hero">
        <h1 className="cust-title">{t.title}</h1>
        <div className="cust-subtitle">預訂你的專屬唱k體驗</div>
      </div>

      <div className="cust-layout">
        <div className="cust-controls">
          <div className="control-group">
            <div className="section-label">
              <Calendar size={14} /> {t.date}
            </div>
            <NeonCalendar
              selectedDate={form.date}
              onSelect={(d) => setForm({ ...form, date: d })}
              t={t}
            />
          </div>

          <div className="control-group">
            <div className="section-label">
              <Clock size={14} /> {t.arrTime}
            </div>
            <input
              type="time"
              className="digital-input"
              value={form.arrTime}
              onChange={(e) => setForm({ ...form, arrTime: e.target.value })}
              style={{ borderColor: errors.time ? "#ff4444" : "" }}
            />
            {errors.time && (
              <div className="warning-text">
                <AlertCircle size={12} /> {errors.time}
              </div>
            )}
            {!errors.time && (
              <div className="session-badge">{form.session}</div>
            )}
          </div>
        </div>

        <div className="cust-content">
          <div className="rooms-area-title">01. {t.roomHeader}</div>
          <div className="rooms-catalog">
            {Object.values(ROOM_TIERS).map((room) => {
              // Comment 2: Skip Walk-in rooms completely in the loop if we don't want to show them
              // But logic in checkAvail handles availability.
              // To fully hide them from UI as per comment "kept and not available for online booking":
              // We can rely on checkAvail returning false for them, but we might want to just render them disabled.
              // However, user said "kept and not available", usually implies hidden or "Call to book".
              // Let's hide them from the loop to be cleaner.
              if (
                WALK_IN_ONLY_ROOMS.some(
                  (id) => ALL_ROOMS.find((r) => r.id === id)?.type === room.id
                )
              ) {
                // This logic is tricky because room tier != room ID.
                // room.id is "small", "b61". WALK_IN_ONLY_ROOMS are physical IDs.
                // The prompt says "Rooms C66, B52 should be kept...". These are physical rooms.
                // Since this loop displays *Room Types* (Small, Medium, etc.), we don't hide the *Type*.
                // The physical room assignment happens in Admin.
                // So for customer, we just check availability count.
                // checkAvail(room.id) counts bookings vs capacity.
                // We should reduce capacity in our heads or just let checkAvail handle it.
                // checkAvail already filters bookings. We just need to ensure the "total capacity" logic
                // inside checkAvail accounts for the fact that some rooms are offline.
                // *Self-correction*: checkAvail currently uses `count < 8`. This is hardcoded.
                // It should be fine as long as we don't overbook.
              }

              // Comment 5: VIP Logic for Day Sessions
              const isDaySession =
                form.session.includes("11:00") ||
                form.session.includes("15:00");
              const isVip = room.id === "vip";
              const isDayVip = isDaySession && isVip;

              const isAvail = checkAvail(room.id);
              const price = getPriceForRoom(
                form.date,
                form.session,
                room.id,
                prices
              );
              const isSelected = form.roomType === room.id;

              return (
                <div
                  key={room.id}
                  className={`cyber-card ${isSelected ? "selected" : ""} ${
                    !isAvail || !price || isDayVip ? "disabled" : ""
                  }`}
                  onClick={() => {
                    if (isDayVip) {
                      // Comment 5: Button unavailable, do nothing or alert?
                      // Class is disabled, so UI shows it.
                      return;
                    }
                    if (isAvail && price)
                      setForm({ ...form, roomType: room.id });
                  }}
                >
                  <div className="cc-header">
                    <div>
                      <div className="cc-name">{room.name}</div>
                      <div className="cc-desc">{room.desc}</div>
                    </div>
                  </div>

                  {/* Logic for Price / Unavailable Label */}
                  {isDayVip ? (
                    <div
                      className="cc-unavailable"
                      style={{ fontSize: "0.8rem", color: "#25d366" }}
                    >
                      請 WhatsApp 聯絡
                    </div>
                  ) : !isAvail && price ? (
                    <div
                      style={{
                        alignSelf: "flex-end",
                        color: "#ff2a2a",
                        fontWeight: "bold",
                        fontSize: "1.2rem",
                      }}
                    >
                      FULL
                    </div>
                  ) : price ? (
                    <div className="cc-price">${price}</div>
                  ) : (
                    <div className="cc-unavailable">{t.unavailable}</div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="cust-form-section">
            <div
              className="rooms-area-title"
              style={{ borderLeftColor: "var(--neon-blue)" }}
            >
              02. 訂座資料
            </div>

            <div className="form-grid-wrapper">
              <div className="form-group">
                <label>{t.name}</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="您的全名 (e.g. 陳小明/John Chan)"
                />
              </div>

              <div className="form-group">
                <label>{t.phone}</label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="1234 5678"
                />
              </div>

              <div className="form-group" style={{ position: "relative" }}>
                <label>{t.pax}</label>
                <input
                  type="number"
                  min="1"
                  value={form.pax}
                  onChange={(e) => setForm({ ...form, pax: e.target.value })}
                  style={{ borderColor: errors.pax ? "#ff4444" : "" }}
                  placeholder="4"
                />
                {errors.pax && (
                  <div className="warning-text">
                    <AlertCircle size={12} /> {errors.pax}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>{t.remark}</label>
                <input
                  value={form.remarks}
                  onChange={(e) =>
                    setForm({ ...form, remarks: e.target.value })
                  }
                  placeholder="如有"
                />
              </div>
            </div>

            <div className="book-action-area">
              <button
                className="mega-btn"
                onClick={submit}
                disabled={loading || !form.roomType || hasErrors}
                style={{
                  opacity: form.roomType && !hasErrors ? 1 : 0.5,
                  cursor:
                    form.roomType && !hasErrors ? "pointer" : "not-allowed",
                }}
              >
                {hasErrors ? "確認預約" : loading ? "請稍等..." : t.book}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================
              6. MODALS
              ========================================= */

function StaffModal({ t, booking, onClose, onSave, bookings = [] }) {
  // 1. Helper: Find an available room that is NOT a Walk-In room
  const getAutoAvailableRoom = (targetDate, targetSession, targetType) => {
    // A. Filter physical rooms by type
    const candidates = ALL_ROOMS.filter((r) => r.type === targetType);

    // B. Find first free room (Excluding Walk-In Only)
    const freeRoom = candidates.find((r) => {
      // Never auto-assign Walk-In rooms
      if (WALK_IN_ONLY_ROOMS.includes(r.id)) return false;

      // Check if occupied
      const isOccupied = bookings.some((b) => {
        if (booking && b.id === booking.id) return false; // Ignore self if editing
        return (
          b.booking_date === targetDate &&
          b.start_time === targetSession &&
          b.assigned_room === r.id
        );
      });

      return !isOccupied;
    });

    return freeRoom ? freeRoom.id : "";
  };

  // 2. Initial State Setup
  // If it's a NEW booking, we try to auto-assign immediately based on defaults
  const initialDate = booking
    ? booking.booking_date
    : new Date().toISOString().split("T")[0];
  const initialTime = booking ? booking.arrival_time || "20:00" : "20:00";
  const initialSession = booking
    ? booking.start_time
    : determineSession("20:00").label; // Default logic
  const initialType = booking ? booking.room_type : "small";

  // Calculate auto-room only if it's a NEW booking (booking is null)
  const defaultAutoRoom = !booking
    ? getAutoAvailableRoom(initialDate, initialSession, initialType)
    : booking.assigned_room || "";

  const [form, setForm] = useState({
    date: initialDate,
    session: initialSession,
    arrTime: initialTime,
    roomType: initialType,
    assigned_room: defaultAutoRoom, // <--- Set initial auto-assigned room
    name: booking ? booking.client_name : "",
    phone: booking ? booking.client_phone : "",
    pax: booking ? booking.pax : 2,
    remarks: booking ? booking.remarks || "" : "",
  });

  const physicalRooms = ALL_ROOMS.filter((r) => r.type === form.roomType);

  const isRoomOccupied = (roomId) =>
    bookings.some((b) => {
      if (booking && b.id === booking.id) return false;
      if (b.booking_date !== form.date) return false;
      if (b.assigned_room !== roomId) return false;
      return b.start_time === form.session;
    });

  // Handle Room Click (Manual Override)
  const handleRoomSelect = (roomId) => {
    if (WALK_IN_ONLY_ROOMS.includes(roomId)) {
      const confirm = window.confirm(
        `房號 ${roomId} 為 Walk-In 預留房。\n確定要分配此房間嗎？`
      );
      if (!confirm) return;
    }
    setForm({ ...form, assigned_room: roomId });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: "950px", height: "auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            borderBottom: "1px solid #333",
            paddingBottom: "15px",
            marginBottom: "20px",
          }}
        >
          <h3 className="glow-text" style={{ margin: 0 }}>
            {booking ? t.edit : t.walkIn}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "white",
              cursor: "pointer",
            }}
          >
            <X />
          </button>
        </div>
        <div className="modal-split-layout">
          <div className="modal-left">
            <NeonCalendar
              selectedDate={form.date}
              onSelect={(d) => {
                // 3. Trigger Auto-Assign on Date Change
                const autoRoom = getAutoAvailableRoom(
                  d,
                  form.session,
                  form.roomType
                );
                setForm({ ...form, date: d, assigned_room: autoRoom });
              }}
              t={t}
              bookings={bookings}
            />
          </div>
          <div className="modal-right">
            <div className="modal-form-container">
              <div className="modal-input-grid">
                <div className="input-group">
                  <label>{t.arrTime}</label>
                  <input
                    type="time"
                    value={form.arrTime}
                    onChange={(e) => {
                      // 4. Trigger Auto-Assign on Time/Session Change
                      const newTime = e.target.value;
                      const newSession = determineSession(newTime).label;
                      const autoRoom = getAutoAvailableRoom(
                        form.date,
                        newSession,
                        form.roomType
                      );
                      setForm({
                        ...form,
                        arrTime: newTime,
                        session: newSession,
                        assigned_room: autoRoom,
                      });
                    }}
                    style={{ fontFamily: "Orbitron", textAlign: "center" }}
                  />
                  {/* NEW: Reminder for Admin */}
                  {parseInt(form.arrTime.split(":")[0]) < 4 && (
                    <div
                      style={{
                        color: "#ffaa00",
                        fontSize: "0.7rem",
                        marginTop: "5px",
                        border: "1px solid #ffaa00",
                        padding: "4px",
                        borderRadius: "4px",
                        background: "rgba(255, 170, 0, 0.1)",
                      }}
                    >
                      <TriangleAlert
                        size={10}
                        style={{ marginRight: 4, verticalAlign: "middle" }}
                      />
                      注意：00:00-04:00 將歸入 <b>{form.date}</b> 的晚間時段
                      (Night Session)。
                    </div>
                  )}
                </div>
                <div className="input-group">
                  <label>{t.session}</label>
                  <div className="read-only-box">{form.session}</div>
                </div>
              </div>
              <div className="modal-input-grid">
                <div className="input-group span-2">
                  <label>{t.roomHeader}</label>
                  <select
                    value={form.roomType}
                    onChange={(e) => {
                      // 5. Trigger Auto-Assign on Room Type Change
                      const newType = e.target.value;
                      const autoRoom = getAutoAvailableRoom(
                        form.date,
                        form.session,
                        newType
                      );
                      setForm({
                        ...form,
                        roomType: newType,
                        assigned_room: autoRoom,
                      });
                    }}
                  >
                    {Object.values(ROOM_TIERS).map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="input-group">
                <label
                  style={{ color: form.assigned_room ? "#00f3ff" : "#666" }}
                >
                  {t.assignRoom}: {form.assigned_room || "未分配"}
                </label>
                <div className="room-picker-grid">
                  {physicalRooms.map((r) => {
                    const occupied = isRoomOccupied(r.id);
                    const isWalkIn = WALK_IN_ONLY_ROOMS.includes(r.id);

                    return (
                      <div
                        key={r.id}
                        onClick={() => {
                          if (!occupied) handleRoomSelect(r.id);
                        }}
                        className={`picker-chip ${
                          form.assigned_room === r.id ? "selected" : ""
                        } ${occupied ? "occupied" : ""}`}
                        style={{
                          borderColor: isWalkIn ? "#ffaa00" : undefined,
                          color:
                            isWalkIn && form.assigned_room !== r.id
                              ? "#ffaa00"
                              : undefined,
                        }}
                      >
                        {r.id}
                      </div>
                    );
                  })}
                  {physicalRooms.length === 0 && (
                    <div
                      style={{
                        color: "#666",
                        textAlign: "center",
                        gridColumn: "1/-1",
                      }}
                    >
                      No rooms available
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-input-grid">
                <div className="input-group">
                  <label>{t.name}</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="input-group">
                  <label>{t.phone}</label>
                  <input
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="modal-input-grid">
                <div className="input-group">
                  <label>{t.pax}</label>
                  <input
                    type="number"
                    min="1"
                    value={form.pax}
                    onChange={(e) => setForm({ ...form, pax: e.target.value })}
                  />
                </div>
                <div className="input-group">
                  <label>{t.remark}</label>
                  <input
                    value={form.remarks}
                    onChange={(e) =>
                      setForm({ ...form, remarks: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button
                  onClick={onClose}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "#222",
                    color: "white",
                    border: "1px solid #444",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  CANCEL
                </button>
                <button
                  onClick={() => {
                    if (!form.name || !form.phone)
                      return alert("名字和電話為必填");
                    // Admin uses "Business Date" directly. No date shifting.
                    onSave(form);
                  }}
                  className="mega-btn"
                  style={{ flex: 2, padding: "12px", marginTop: 0 }}
                >
                  {booking ? t.save : t.save}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PricingModal({ onClose, prices, onSaveSuccess }) {
  const [isEditing, setIsEditing] = useState(false);
  const [localPrices, setLocalPrices] = useState(prices || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (prices) setLocalPrices(prices);
  }, [prices]);

  const handleChange = (index, field, value) => {
    const updated = [...localPrices];
    updated[index] = {
      ...updated[index],
      [field]: value === "" ? null : parseInt(value),
    };
    setLocalPrices(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase.from("pricing").upsert(localPrices);
    setSaving(false);
    if (error) alert("Error: " + error.message);
    else {
      setIsEditing(false);
      if (onSaveSuccess) onSaveSuccess();
    }
  };

  const renderCell = (row, field) => {
    if (!row) return "-";
    if (isEditing)
      return (
        <input
          type="number"
          value={row[field] ?? ""}
          onChange={(e) =>
            handleChange(localPrices.indexOf(row), field, e.target.value)
          }
          style={{
            width: "100%",
            background: "#111",
            border: "1px solid #444",
            color: "white",
            padding: "4px",
            textAlign: "center",
            borderRadius: "3px",
          }}
        />
      );
    return row[field] ? (
      `$${row[field]}`
    ) : (
      <span style={{ color: "#444" }}>/</span>
    );
  };

  const getRow = (cat, sess) =>
    localPrices.find((p) => p.category === cat && p.session === sess) || {};

  return (
    <div className="modal-overlay">
      <div
        className="modal-box"
        style={{ maxWidth: "1000px", padding: "25px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1.5rem" }} className="glow-text">
            價目表 PRICING
          </h2>
          <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="btn-compact"
              >
                <Edit2 size={14} /> 更改價錢
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={saving}
                className={`btn-compact ${saving ? "saving" : ""}`}
              >
                {saving ? "SAVING..." : "儲存 SAVE"}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                color: "#666",
                cursor: "pointer",
                padding: "5px",
              }}
            >
              <X size={24} />
            </button>
          </div>
        </div>
        <div className="pricing-table-container">
          <table className="pricing-table">
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead>
              <tr>
                <th
                  className="pricing-th"
                  style={{ textAlign: "left", paddingLeft: 15 }}
                >
                  日子
                </th>
                <th className="pricing-th">時段</th>
                <th className="pricing-th">細房</th>
                <th className="pricing-th">中房</th>
                <th className="pricing-th">大房</th>
                <th className="pricing-th">VIP</th>
              </tr>
            </thead>
            <tbody>
              <PriceRow
                label="Weekday (平日)"
                rowDay={getRow("weekday", "day")}
                rowNight={getRow("weekday", "night")}
                renderCell={renderCell}
              />
              <PriceRow
                label="Friday (週五)"
                rowDay={getRow("friday", "day")}
                rowNight={getRow("friday", "night")}
                renderCell={renderCell}
              />
              <PriceRow
                label="Saturday (週六)"
                rowDay={getRow("saturday", "day")}
                rowNight={getRow("saturday", "night")}
                renderCell={renderCell}
              />
              <PriceRow
                label="Sunday (週日)"
                rowDay={getRow("sunday", "day")}
                rowNight={getRow("sunday", "night")}
                renderCell={renderCell}
              />
              <tr
                className="pricing-row"
                style={{ borderTop: "2px solid #444" }}
              >
                <td rowSpan={2} className="pricing-td pricing-td-cat">
                  Holiday Eve (假前)
                </td>
                <td
                  className="pricing-td"
                  style={{ color: "#888", lineHeight: "1.4" }}
                >
                  <div>11:00 - 15:00</div>
                  <div>15:00 - 19:00</div>
                </td>
                <td colSpan={5} className="pricing-td merged-cell">
                  根據星期幾，跟隨上述價錢
                </td>
              </tr>
              <tr className="pricing-row">
                <td
                  className="pricing-td"
                  style={{ color: "#bc13fe", fontWeight: "bold" }}
                >
                  19:00 - 04:00
                </td>
                <td className="pricing-td">
                  {renderCell(getRow("holidayEve", "night"), "small")}
                </td>
                <td className="pricing-td">
                  {renderCell(getRow("holidayEve", "night"), "medium")}
                </td>
                <td className="pricing-td">
                  {renderCell(getRow("holidayEve", "night"), "large")}
                </td>
                <td className="pricing-td">
                  {renderCell(getRow("holidayEve", "night"), "vip")}
                </td>
              </tr>
              <tr
                className="pricing-row"
                style={{ borderTop: "2px solid #444" }}
              >
                <td rowSpan={2} className="pricing-td pricing-td-cat">
                  Holiday (假期)
                </td>
                <td
                  className="pricing-td"
                  style={{
                    color: "#00f3ff",
                    fontWeight: "bold",
                    lineHeight: "1.4",
                  }}
                >
                  <div>11:00 - 15:00</div>
                  <div>15:00 - 19:00</div>
                </td>
                <td className="pricing-td">
                  {renderCell(getRow("holiday", "day"), "small")}
                </td>
                <td className="pricing-td">
                  {renderCell(getRow("holiday", "day"), "medium")}
                </td>
                <td className="pricing-td">
                  {renderCell(getRow("holiday", "day"), "large")}
                </td>
                <td className="pricing-td">
                  {renderCell(getRow("holiday", "day"), "vip")}
                </td>
              </tr>
              <tr className="pricing-row">
                <td className="pricing-td" style={{ color: "#888" }}>
                  19:00 - 04:00
                </td>
                <td colSpan={5} className="pricing-td merged-cell">
                  根據星期幾，跟隨上述價錢
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const PriceRow = ({ label, rowDay, rowNight, renderCell }) => (
  <>
    <tr className="pricing-row" style={{ borderTop: "1px solid #333" }}>
      <td rowSpan={2} className="pricing-td pricing-td-cat">
        {label}
      </td>
      <td
        className="pricing-td"
        style={{ color: "#00f3ff", lineHeight: "1.4" }}
      >
        <div>11:00 - 15:00</div>
        <div>15:00 - 19:00</div>
      </td>
      <td className="pricing-td">{renderCell(rowDay, "small")}</td>
      <td className="pricing-td">{renderCell(rowDay, "medium")}</td>
      <td className="pricing-td">{renderCell(rowDay, "large")}</td>
      <td className="pricing-td">{renderCell(rowDay, "vip")}</td>
    </tr>
    <tr className="pricing-row">
      <td className="pricing-td" style={{ color: "#bc13fe" }}>
        19:00 - 04:00
      </td>
      <td className="pricing-td">{renderCell(rowNight, "small")}</td>
      <td className="pricing-td">{renderCell(rowNight, "medium")}</td>
      <td className="pricing-td">{renderCell(rowNight, "large")}</td>
      <td className="pricing-td">{renderCell(rowNight, "vip")}</td>
    </tr>
  </>
);

/* =========================================
              7. SUB-COMPONENTS (WIDGETS)
              ========================================= */

// Comment 3: Filter Props (filter, onFilterChange) added
function RoomStatusGrid({ date, bookings, t, filter, onFilterChange }) {
  const todaysBookings = bookings.filter((b) => b.booking_date === date);

  const FILTER_OPTS = [
    { id: "ALL", label: "ALL" },
    { id: "small", label: "細房" },
    { id: "medium", label: "中房" },
    { id: "large", label: "大房" },
    { id: "vip", label: "VIP" },
  ];

  const getLabel = (id) =>
    FILTER_OPTS.find((opt) => opt.id === id)?.label || id.toUpperCase();
  const getRoomsByCategory = (catId) =>
    catId === "vip"
      ? ALL_ROOMS.filter((r) => r.type === "vip")
      : ALL_ROOMS.filter((r) => r.type === catId);
  const getRoomDots = (roomId) => {
    const assigned = todaysBookings.filter((b) => b.assigned_room === roomId);
    return {
      lunch: assigned.some((b) => b.start_time.startsWith("11:00")),
      afternoon: assigned.some((b) => b.start_time.startsWith("15:00")),
      night: assigned.some((b) => b.start_time.startsWith("19:00")),
    };
  };

  const renderRoomGrid = (rooms) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(65px, 1fr))",
        gap: "6px",
      }}
    >
      {rooms.map((room) => {
        const dots = getRoomDots(room.id);
        const isEmpty = !dots.lunch && !dots.afternoon && !dots.night;
        return (
          <div
            key={room.id}
            style={{
              background: "#161616",
              border: "1px solid #333",
              borderRadius: "4px",
              padding: "4px 2px",
              textAlign: "center",
              opacity: isEmpty ? 0.5 : 1,
            }}
          >
            <div
              style={{
                fontSize: "0.8rem",
                fontWeight: "bold",
                color: "#fff",
                marginBottom: "3px",
                letterSpacing: "1px",
              }}
            >
              {room.id}
            </div>
            <div
              style={{ display: "flex", justifyContent: "center", gap: "3px" }}
            >
              <div
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: dots.lunch ? "#00f3ff" : "#2a2a2a",
                  boxShadow: dots.lunch ? "0 0 5px #00f3ff" : "none",
                }}
              ></div>
              <div
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: dots.afternoon ? "#0066ff" : "#2a2a2a",
                  boxShadow: dots.afternoon ? "0 0 5px #0066ff" : "none",
                }}
              ></div>
              <div
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: dots.night ? "#bc13fe" : "#2a2a2a",
                  boxShadow: dots.night ? "0 0 5px #bc13fe" : "none",
                }}
              ></div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="panel room-status-panel">
      <div className="room-status-header">
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <h3
            style={{
              margin: 0,
              fontSize: "0.9rem",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Grid size={16} color="var(--neon-cyan)" /> {t.status}
          </h3>
          <div
            style={{
              display: "flex",
              gap: "10px",
              fontSize: "0.65rem",
              color: "#888",
              alignItems: "center",
              borderLeft: "1px solid #333",
              paddingLeft: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
              <div
                style={{
                  width: 5,
                  height: 5,
                  background: "#00f3ff",
                  borderRadius: "50%",
                }}
              ></div>{" "}
              11-15
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
              <div
                style={{
                  width: 5,
                  height: 5,
                  background: "#0066ff",
                  borderRadius: "50%",
                }}
              ></div>{" "}
              15-19
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
              <div
                style={{
                  width: 5,
                  height: 5,
                  background: "#bc13fe",
                  borderRadius: "50%",
                }}
              ></div>{" "}
              19-04
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "2px",
            background: "#000",
            padding: "2px",
            borderRadius: "4px",
            border: "1px solid #333",
          }}
        >
          {FILTER_OPTS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => onFilterChange(opt.id)}
              style={{
                border: "none",
                background:
                  filter === opt.id ? "var(--neon-cyan)" : "transparent",
                color: filter === opt.id ? "black" : "#666",
                padding: "2px 8px",
                borderRadius: "3px",
                fontSize: "0.65rem",
                cursor: "pointer",
                fontWeight: "bold",
                textTransform: "uppercase",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className="room-status-content">
        {filter === "ALL" ? (
          <>
            <div style={{ flex: 1 }}>
              <h4 style={headerStyle}>細房</h4>
              {renderRoomGrid(getRoomsByCategory("small"))}
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={headerStyle}>中房</h4>
              {renderRoomGrid(getRoomsByCategory("medium"))}
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <div>
                <h4 style={headerStyle}>大房</h4>
                {renderRoomGrid(getRoomsByCategory("large"))}
              </div>
              <div>
                <h4 style={headerStyle}>VIP</h4>
                {renderRoomGrid(getRoomsByCategory("vip"))}
              </div>
            </div>
          </>
        ) : (
          <div style={{ width: "100%" }}>
            <h4 style={headerStyle}>{getLabel(filter)}</h4>
            {renderRoomGrid(getRoomsByCategory(filter))}
          </div>
        )}
      </div>
    </div>
  );
}

const headerStyle = {
  margin: "0 0 6px 0",
  color: "#666",
  fontSize: "0.7rem",
  borderBottom: "1px solid #333",
  fontFamily: '"Noto Sans TC", sans-serif',
};

function NeonCalendar({ selectedDate, onSelect, t, bookings = [] }) {
  const [currentDate, setCurrentDate] = useState(new Date(selectedDate));
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const slots = Array(firstDay)
    .fill(null)
    .concat([...Array(daysInMonth).keys()].map((i) => i + 1));
  const formatDate = (d) =>
    `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(
      2,
      "0"
    )}`;

  return (
    <div className="cal-wrapper">
      <div className="cal-header">
        <button
          className="cal-nav-btn"
          onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
        >
          <ChevronLeft size={16} />
        </button>
        <span>
          {year} / {month + 1}
        </span>
        <button
          className="cal-nav-btn"
          onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="cal-grid">
        {t.days.map((d) => (
          <div key={d} className="cal-day-header">
            {d}
          </div>
        ))}
        {slots.map((day, idx) => {
          if (!day) return <div key={idx} className="cal-cell empty"></div>;
          const dateStr = formatDate(day);
          const isSelected = dateStr === selectedDate;
          const hasBooking = bookings.some((b) => b.booking_date === dateStr);
          const isHoliday = HK_HOLIDAYS.includes(dateStr);
          return (
            <div
              key={idx}
              className={`cal-cell ${isSelected ? "selected" : ""} ${
                dateStr === new Date().toISOString().split("T")[0]
                  ? "today"
                  : ""
              }`}
              onClick={() => onSelect(dateStr)}
            >
              <span
                style={{
                  color: isHoliday && !isSelected ? "#ff4444" : "inherit",
                }}
              >
                {day}
              </span>
              {hasBooking && !isSelected && <div className="cal-dot"></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PendingModal({
  onClose,
  bookings,
  confirmedBookings,
  onApprove,
  onDelete,
  prices,
  setTooltipData,
}) {
  const [assignments, setAssignments] = useState({});

  // 1. Updated Toggle Logic with Walk-in Warning
  // 1. Updated Toggle Logic (Fixed Double Alert)
  const toggleAssignment = (bookingId, roomId) => {
    // Check current state before calling setAssignments
    // This prevents the window.confirm from running during the state calculation phase
    const currentAssigned = assignments[bookingId];
    const isSelecting = currentAssigned !== roomId;

    if (isSelecting) {
      if (WALK_IN_ONLY_ROOMS.includes(roomId)) {
        const confirmAssign = window.confirm(
          `房號 ${roomId} 為 Walk-In 預留房 (Reserved)。\n確定要分配給網上預約嗎？`
        );
        if (!confirmAssign) return; // Stop here if cancelled
      }
    }

    // Safe to update state now
    setAssignments((prev) => ({
      ...prev,
      [bookingId]: isSelecting ? roomId : null,
    }));
  };

  const constructWhatsAppUrl = (b) => {
    const phone = b.client_phone.replace(/\D/g, "");
    // Get Room Data
    const roomData = ROOM_TIERS[b.room_type];
    const roomName = roomData?.name || b.room_type;
    const capacity = roomData?.capacity || "-";

    // Construct Message
    const msg =
      `您好 ${b.client_name}，您在SugarCEO的預訂已成功確認，到達時請出示此訊息以入座\n\n` +
      `預訂資料:\n` +
      `Ref: #${b.id}\n` +
      `日期： ${b.booking_date}\n` +
      `時間： ${b.arrival_time}\n` +
      `房型： ${roomName}\n` +
      `人數： ${b.pax}位 (上限 ${capacity}位)\n\n` +
      `期待您的來臨！`;

    return `https://wa.me/852${phone}?text=${encodeURIComponent(msg)}`;
  };

  const handleAction = (action, ...args) => {
    setTooltipData(null);
    action(...args);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: "700px", padding: "20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            borderBottom: "1px solid #333",
            paddingBottom: 10,
          }}
        >
          <h3
            className="glow-text"
            style={{ margin: 0, color: "var(--amber-alert)" }}
          >
            新預約
          </h3>
          <button
            onClick={() => handleAction(onClose)}
            style={{
              background: "none",
              border: "none",
              color: "white",
              cursor: "pointer",
            }}
          >
            <X />
          </button>
        </div>

        <div className="pending-list-container">
          {bookings.length === 0 && (
            <div style={{ textAlign: "center", color: "#666", padding: 20 }}>
              沒有新預約。
            </div>
          )}

          {bookings.map((b) => {
            const roomTypeName = ROOM_TIERS[b.room_type]?.name || b.room_type;
            const price = getPriceForRoom(
              b.booking_date,
              b.start_time,
              b.room_type,
              prices
            );

            // Get rooms matching the requested type
            const relevantRooms = ALL_ROOMS.filter(
              (r) => r.type === b.room_type
            );

            const checkOccupancy = (roomId) => {
              return confirmedBookings.some(
                (confirmed) =>
                  confirmed.booking_date === b.booking_date &&
                  confirmed.start_time === b.start_time &&
                  confirmed.assigned_room === roomId
              );
            };

            // 2. Determine "Auto Assignable" Room
            // MUST be free AND NOT in WALK_IN_ONLY_ROOMS
            const autoAssignableRoom = relevantRooms.find(
              (r) => !checkOccupancy(r.id) && !WALK_IN_ONLY_ROOMS.includes(r.id)
            );

            // 3. Determine Final Target Room (Manual > Auto)
            const manualSelection = assignments[b.id];

            // 4. Can we approve?
            // Yes if: User manually selected a room OR an auto-assign room exists.
            // If online rooms are full (autoAssignableRoom undefined) and no manual selection, DISABLE button.
            const canApprove = !!manualSelection || !!autoAssignableRoom;

            const handleApproveClick = () => {
              if (!canApprove) return;

              window.open(constructWhatsAppUrl(b), "_blank");

              // Use manual selection if exists, otherwise fallback to auto room
              const finalRoom =
                manualSelection ||
                (autoAssignableRoom ? autoAssignableRoom.id : null);

              onApprove(b.id, finalRoom);
            };

            return (
              <div key={b.id} style={{ marginBottom: 15 }}>
                <div
                  className="hud-card pending-layout"
                  style={{
                    "--card-theme": "var(--amber-alert)",
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipData({
                      booking: b,
                      roomTypeName,
                      rect: rect,
                      color: "var(--amber-alert)",
                      price: price,
                    });
                  }}
                  onMouseLeave={() => setTooltipData(null)}
                >
                  <div
                    className="hud-time"
                    style={{ background: "rgba(255, 170, 0, 0.1)" }}
                  >
                    <div
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      {b.booking_date.slice(5)}
                    </div>
                    <div
                      className="hud-time-text"
                      style={{ fontSize: "1.1rem", marginTop: 2 }}
                    >
                      {b.arrival_time}
                    </div>
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "var(--amber-alert)",
                        marginTop: 2,
                        fontWeight: "bold",
                      }}
                    >
                      {roomTypeName}
                    </div>
                  </div>

                  <div className="hud-info">
                    <div className="info-header">
                      <span className="client-name">{b.client_name}</span>
                      <span
                        className="room-number-badge"
                        style={{
                          color: assignments[b.id]
                            ? "var(--neon-cyan)"
                            : "#888",
                        }}
                      >
                        {assignments[b.id]
                          ? `手動選擇: ${assignments[b.id]}`
                          : autoAssignableRoom
                          ? `自動選擇: ${autoAssignableRoom.id}`
                          : "沒有可用房間"}
                      </span>
                    </div>
                    <div className="info-details">
                      <span>{b.pax} 人</span>
                      <span style={{ opacity: 0.3 }}>|</span>
                      <span style={{ fontFamily: "monospace" }}>
                        {b.client_phone}
                      </span>
                      {b.remarks && (
                        <>
                          <span style={{ opacity: 0.3 }}>|</span>
                          <div className="icon-alert-only">
                            <TriangleAlert size={14} />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="pending-actions">
                    <button
                      className="p-btn del"
                      onClick={() => handleAction(onDelete, b.id)}
                      title="Reject"
                    >
                      <Trash2 size={16} />
                    </button>
                    <button
                      className="p-btn approve"
                      onClick={handleApproveClick}
                      disabled={!canApprove}
                      style={{
                        opacity: canApprove ? 1 : 0.3,
                        cursor: canApprove ? "pointer" : "not-allowed",
                      }}
                      title={
                        canApprove
                          ? "Approve (Open WhatsApp & Auto-Assign)"
                          : "Full - Select Walk-in Room Manually"
                      }
                    >
                      <Check size={20} />
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    background: "#0a0a0a",
                    border: "1px solid var(--amber-alert)",
                    borderTop: "none",
                    borderBottomLeftRadius: 6,
                    borderBottomRightRadius: 6,
                    padding: "10px 15px",
                  }}
                >
                  <div className="pending-room-label">
                    <span>房間分配</span>
                    <span
                      style={{
                        color: assignments[b.id] ? "var(--neon-cyan)" : "#666",
                      }}
                    >
                      {assignments[b.id]
                        ? `Selected: ${assignments[b.id]}`
                        : "如未選擇，將自動分配可用房間，不會自動選擇 B52 及 C66。"}
                    </span>
                  </div>

                  <div className="mini-room-grid">
                    {relevantRooms.map((r) => {
                      const isOccupied = checkOccupancy(r.id);
                      const isSelected = assignments[b.id] === r.id;
                      // Highlight walk-in rooms differently to hint the user
                      const isWalkIn = WALK_IN_ONLY_ROOMS.includes(r.id);

                      return (
                        <div
                          key={r.id}
                          className={`mini-chip ${
                            isSelected ? "selected" : ""
                          } ${isOccupied ? "occupied" : ""}`}
                          style={{
                            borderColor: isWalkIn ? "#ffaa00" : undefined,
                            color:
                              isWalkIn && !isSelected ? "#ffaa00" : undefined,
                          }}
                          onClick={() =>
                            !isOccupied && toggleAssignment(b.id, r.id)
                          }
                          title={isWalkIn ? "Walk-In Room" : ""}
                        >
                          {r.id}
                        </div>
                      );
                    })}
                    {relevantRooms.length === 0 && (
                      <div style={{ color: "#666", fontSize: "0.7rem" }}>
                        No rooms found for {roomTypeName}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default AppContainer;
